/**
 * Shared storage helpers for VRS auth/session data.
 *
 * We mirror auth data into both localStorage and sessionStorage so existing
 * pages keep working while login persists across browser restarts. React Native
 * does not expose those browser globals, so native builds also use AsyncStorage
 * and hydrate an in-memory cache before session validation/queue auth.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
const memoryStorage = new Map();
const blockedKeys = new Set();
const keyGenerations = new Map();
let storageGeneration = 0;
const nativeMutations = new Map();
/** Fence retired native credentials before any asynchronous sign-out work. */
export function blockPersistentItems(keys) {
    storageGeneration++;
    keys.forEach(key => {
        blockedKeys.add(key);
        memoryStorage.delete(key);
        keyGenerations.set(key, (keyGenerations.get(key) || 0) + 1);
    });
}
export function unblockPersistentItems(keys) {
    storageGeneration++;
    keys.forEach(key => {
        blockedKeys.delete(key);
        keyGenerations.set(key, (keyGenerations.get(key) || 0) + 1);
    });
}
export function isPersistentItemBlocked(key) {
    return blockedKeys.has(key);
}
export function getStorageGeneration() {
    return storageGeneration;
}
function enqueueNativeMutation(key, operation) {
    const pending = (nativeMutations.get(key) || Promise.resolve()).catch(() => undefined).then(operation);
    nativeMutations.set(key, pending);
    void pending.finally(() => {
        if (nativeMutations.get(key) === pending)
            nativeMutations.delete(key);
    }).catch(() => undefined);
    return pending;
}
// Tab-scoped keys prefer this tab's sessionStorage on read, but may still seed
// a default from the shared localStorage mirror (e.g. remember the last camera
// a returning user picked). That default is harmless for device selection.
const TAB_SCOPED_BROWSER_KEYS = new Set([
    'vrs_user_role',
    'vrs_camera_device_id',
    'vrs_camera_device_label',
    'vrs_camera_device_group_id',
    'vrs_microphone_device_id',
    'vrs_microphone_device_label',
    'vrs_microphone_device_group_id'
]);
// Session-only keys must NEVER touch the shared localStorage mirror. localStorage
// is shared across every tab of the same origin, so mirroring the role there lets
// a freshly opened tab inherit another tab's role — e.g. a client tab starts
// behaving as an interpreter. These stay in this tab's sessionStorage (plus the
// in-memory/native cache for React Native, which has no multi-tab concept).
const SESSION_ONLY_BROWSER_KEYS = new Set([
    'vrs_user_role'
]);
function getNativeStorage() {
    return AsyncStorage;
}
function getLocalStorage() {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : undefined;
    }
    catch {
        return undefined;
    }
}
function getSessionStorage() {
    try {
        return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
    }
    catch {
        return undefined;
    }
}
function safeGet(storage, key) {
    if (!storage) {
        return null;
    }
    try {
        return storage.getItem(key);
    }
    catch {
        return null;
    }
}
function safeSet(storage, key, value) {
    if (!storage) {
        return;
    }
    try {
        storage.setItem(key, value);
    }
    catch {
        // Ignore storage write failures (private mode, quotas, etc.)
    }
}
function safeRemove(storage, key) {
    if (!storage) {
        return;
    }
    try {
        storage.removeItem(key);
    }
    catch {
        // Ignore storage remove failures.
    }
}
async function safeNativeGet(key) {
    const storage = getNativeStorage();
    if (!storage) {
        return null;
    }
    try {
        return await storage.getItem(key);
    }
    catch {
        return null;
    }
}
async function safeNativeSet(key, value) {
    const storage = getNativeStorage();
    if (!storage) {
        return;
    }
    try {
        const generation = keyGenerations.get(key);
        await enqueueNativeMutation(key, async () => {
            if (blockedKeys.has(key) || generation !== keyGenerations.get(key))
                return;
            await storage.setItem(key, value);
        });
    }
    catch {
        // Ignore native storage write failures.
    }
}
async function safeNativeRemove(key) {
    const storage = getNativeStorage();
    if (!storage) {
        return;
    }
    try {
        await enqueueNativeMutation(key, () => storage.removeItem(key));
    }
    catch {
        // Ignore native storage remove failures.
    }
}
// Caption preferences have confirmed write/readback and explicit Retry. A read
// of their unchanged browser mirror must not start a competing best-effort write;
// all other keys retain the legacy read-triggered native resync behavior.
export function getPersistentItem(key) {
    if (blockedKeys.has(key))
        return null;
    const session = safeGet(getSessionStorage(), key);
    if (TAB_SCOPED_BROWSER_KEYS.has(key) && session !== null) {
        const changed = memoryStorage.get(key) !== session;
        memoryStorage.set(key, session);
        if (changed || key !== 'vrs_caption_preferences')
            void safeNativeSet(key, session);
        return session;
    }
    // Never fall back to the shared localStorage mirror for session-only keys.
    if (SESSION_ONLY_BROWSER_KEYS.has(key)) {
        return session ?? memoryStorage.get(key) ?? null;
    }
    const local = safeGet(getLocalStorage(), key);
    if (local !== null) {
        safeSet(getSessionStorage(), key, local);
        const changed = memoryStorage.get(key) !== local;
        memoryStorage.set(key, local);
        if (changed || key !== 'vrs_caption_preferences')
            void safeNativeSet(key, local);
        return local;
    }
    if (session !== null) {
        safeSet(getLocalStorage(), key, session);
        const changed = memoryStorage.get(key) !== session;
        memoryStorage.set(key, session);
        if (changed || key !== 'vrs_caption_preferences')
            void safeNativeSet(key, session);
        return session;
    }
    return memoryStorage.get(key) ?? null;
}
/**
 * Read a key with getPersistentItem's precedence but none of its side effects:
 * no re-mirroring into sessionStorage/localStorage and no AsyncStorage write.
 * For hot read-only paths such as Redux selectors and mapStateToProps, which
 * run on every store update.
 */
export function peekPersistentItem(key) {
    if (blockedKeys.has(key))
        return null;
    const session = safeGet(getSessionStorage(), key);
    if (TAB_SCOPED_BROWSER_KEYS.has(key) && session !== null) {
        return session;
    }
    if (SESSION_ONLY_BROWSER_KEYS.has(key)) {
        return session ?? memoryStorage.get(key) ?? null;
    }
    return safeGet(getLocalStorage(), key) ?? session ?? memoryStorage.get(key) ?? null;
}
/**
 * Read a browser-local preference without resurrecting a removed value from
 * session/memory mirrors. Use only for preferences, never auth/session state.
 * When browser storage is blocked, retain the existing best-effort fallback.
 */
export function getBrowserPreferenceItem(key) {
    const local = getLocalStorage();
    if (local) {
        try {
            const value = local.getItem(key);
            if (value === null) {
                memoryStorage.delete(key);
                safeRemove(getSessionStorage(), key);
            }
            else {
                memoryStorage.set(key, value);
                safeSet(getSessionStorage(), key, value);
            }
            return value;
        }
        catch {
            // A blocked read is not evidence that the preference was removed.
        }
    }
    return getPersistentItem(key);
}
export async function getPersistentItemAsync(key) {
    if (blockedKeys.has(key))
        return null;
    const generation = keyGenerations.get(key);
    const cached = getPersistentItem(key);
    if (cached !== null) {
        return cached;
    }
    const native = await safeNativeGet(key);
    if (blockedKeys.has(key) || generation !== keyGenerations.get(key))
        return null;
    if (native !== null) {
        memoryStorage.set(key, native);
        if (!SESSION_ONLY_BROWSER_KEYS.has(key)) {
            safeSet(getLocalStorage(), key, native);
        }
        safeSet(getSessionStorage(), key, native);
        return native;
    }
    return null;
}
export async function hydratePersistentItems(keys) {
    await Promise.all(keys.map(key => getPersistentItemAsync(key)));
}
const confirmedPreferenceWrites = new Map();
export function setPersistentItem(key, value, options) {
    if (blockedKeys.has(key)) {
        return options?.confirm ? Promise.reject(new Error('Session storage is retired.')) : undefined;
    }
    memoryStorage.set(key, value);
    if (!SESSION_ONLY_BROWSER_KEYS.has(key)) {
        safeSet(getLocalStorage(), key, value);
    }
    safeSet(getSessionStorage(), key, value);
    if (options?.confirm) {
        // Device preferences apply immediately, but a UI must not claim durable
        // success before write/readback. Serializing preserves the latest choice
        // even when the native bridge resolves writes out of order.
        const previous = confirmedPreferenceWrites.get(key) || Promise.resolve();
        const write = previous.catch(() => undefined).then(async () => {
            if (memoryStorage.get(key) !== value)
                throw new Error('Preference was superseded.');
            const storage = getNativeStorage();
            if (!storage)
                throw new Error('Preference storage is unavailable.');
            await storage.setItem(key, value);
            if (await storage.getItem(key) !== value)
                throw new Error('Preference save was not confirmed.');
        });
        confirmedPreferenceWrites.set(key, write);
        void write.finally(() => {
            if (confirmedPreferenceWrites.get(key) === write)
                confirmedPreferenceWrites.delete(key);
        }).catch(() => undefined);
        return write;
    }
    void safeNativeSet(key, value);
}
/**
 * Confirmed persistence for retry identities. These helpers deliberately bypass
 * the best-effort cache: a failed read is not evidence that no operation exists.
 * AsyncStorage uses localStorage on web and the native durable store on mobile.
 */
export async function getDurableKeys(prefix) {
    const keys = await AsyncStorage.getAllKeys();
    return keys.filter(key => key.startsWith(prefix));
}
export async function getDurableItem(key) {
    return AsyncStorage.getItem(key);
}
export async function setDurableItem(key, value) {
    await AsyncStorage.setItem(key, value);
}
export async function removeDurableItem(key) {
    await AsyncStorage.removeItem(key);
}
export function removePersistentItem(key) {
    memoryStorage.delete(key);
    safeRemove(getLocalStorage(), key);
    safeRemove(getSessionStorage(), key);
    void safeNativeRemove(key);
}
export function clearPersistentItems(keys) {
    keys.forEach(removePersistentItem);
}
/** Native sign-out must wait for earlier writes and confirm every credential removal. */
export async function removePersistentItemConfirmed(key) {
    memoryStorage.delete(key);
    await confirmedPreferenceWrites.get(key)?.catch(() => undefined);
    for (const storage of [getLocalStorage(), getSessionStorage()]) {
        if (!storage)
            continue;
        storage.removeItem(key);
        if (storage.getItem(key) !== null)
            throw new Error('Session removal was not confirmed.');
    }
    await enqueueNativeMutation(key, async () => {
        const storage = getNativeStorage();
        if (!storage)
            throw new Error('Session storage is unavailable.');
        await storage.removeItem(key);
        if (await storage.getItem(key) !== null)
            throw new Error('Session removal was not confirmed.');
    });
}
export function getPersistentJson(key) {
    const value = getPersistentItem(key);
    if (!value) {
        return null;
    }
    try {
        return JSON.parse(value);
    }
    catch {
        return null;
    }
}
export async function getPersistentJsonAsync(key) {
    const value = await getPersistentItemAsync(key);
    if (!value) {
        return null;
    }
    try {
        return JSON.parse(value);
    }
    catch {
        return null;
    }
}
export function hasPersistentAuthToken() {
    const auth = getPersistentJson('vrs_auth_token');
    return Boolean(auth?.token);
}
const PENDING_ROOM_REDIRECT_KEY = 'vrs_pending_room_redirect';
export function setPendingRoomRedirect(url) {
    setPersistentItem(PENDING_ROOM_REDIRECT_KEY, url);
}
export function getPendingRoomRedirect() {
    return getPersistentItem(PENDING_ROOM_REDIRECT_KEY);
}
export function consumePendingRoomRedirect() {
    const url = getPendingRoomRedirect();
    if (url) {
        removePersistentItem(PENDING_ROOM_REDIRECT_KEY);
    }
    return url;
}
// Cast rather than augment the global Window: the native build types `window`
// as its own IWindow, so a `declare global` here fails tsc:native for a bridge
// that only exists on web.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    window.__vrsStorage = {
        getPersistentItem,
        removePersistentItem,
        setPersistentItem
    };
}
